import {
    Block,
    CHILD_OBJECT_INVALID,
    CHILD_TYPE_INVALID,
    NODE_DATA_INVALID,
    PARENT_OBJECT_INVALID,
    PARENT_TYPE_INVALID,
    Schema,
} from '@gitbook/slate';
import { assert } from '@gitbook/util-ts';

import { BLOCK_UPDATE, BLOCK_UPDATES, BLOCK_UPDATES_FORMATS } from '../types';
import { unwrapChildren, wrapInlinesInParagraph } from '../utils';
import { normalizeDate, normalizeTags, validateDateAsYYYYMMDD } from './utils';

export const schema = Schema.create({
    blocks: {
        [BLOCK_UPDATES]: {
            parent: { objects: ['document', 'block', 'fragment'] },
            nodes: [{ types: [BLOCK_UPDATE] }],
            isVoid: false,
            data: {
                format: (format) => format === undefined || BLOCK_UPDATES_FORMATS.includes(format),
            },
            normalize: Schema.createNormalize({
                [PARENT_OBJECT_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [PARENT_TYPE_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [CHILD_TYPE_INVALID]: (change, context) => {
                    assert(Block.isBlock(context.node));

                    const updates = context.node;

                    if (updates.nodes.size === 1) {
                        return change.removeNodeByKey(updates.key);
                    }

                    return change.wrapBlockByKey(
                        context.child.key,
                        Block.create({ type: BLOCK_UPDATE })
                    );
                },
            }),
        },
        [BLOCK_UPDATE]: {
            parent: { types: [BLOCK_UPDATES] },
            nodes: [{ objects: ['block'], min: 1 }],
            isVoid: false,
            data: {
                date: validateDateAsYYYYMMDD,
                /**
                 * Validate tags are an array of strings (or undefined).
                 * String tags (from hyperscript) will trigger normalization to arrays.
                 */
                tags: (tags) => tags === undefined || Array.isArray(tags),
            },
            normalize: Schema.createNormalize({
                [PARENT_OBJECT_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [PARENT_TYPE_INVALID]: (change, context) => unwrapChildren(change, context.node),
                [CHILD_OBJECT_INVALID]: (change, context) => {
                    if (!('key' in context.node)) {
                        return;
                    }
                    return wrapInlinesInParagraph(change, context.node.key);
                },
                [NODE_DATA_INVALID]: (change, context) => {
                    // @ts-expect-error - context.key is available for NODE_DATA_INVALID
                    const { node, key } = context;
                    // Normalize dates so we know they are in YYYY-MM-DD format
                    if (key === 'date' && Block.isBlock(node)) {
                        const providedDate = node.data.get(key);
                        const dateYYYYMMDD = normalizeDate(providedDate);
                        change.setDataByKey(node.key, [key], dateYYYYMMDD);
                    }
                    // Normalize tags from strings to arrays (e.g., from hyperscript)
                    if (key === 'tags' && Block.isBlock(node)) {
                        const providedTags = node.data.get('tags');
                        // Only normalize if it's a string (not already an array)
                        if (typeof providedTags === 'string') {
                            const normalizedTags = normalizeTags(providedTags);
                            change.setDataByKey(node.key, [key], normalizedTags);
                        }
                    }
                },
            }),
        },
    },
});
